"use client";

import { useState } from "react";
import Sentry from "@/lib/sentry-safe";
import { useDownload } from "@/hooks/useDownload";
import { soundsApi, type Sound } from "@/lib/supabase";
import { storageApi } from "@/lib/api/storage";
import {
  TESLA_REQUIREMENTS,
  convertToWav,
  decodeAudioFile,
  trimAudio,
} from "@/lib/audio-utils";

/**
 * What happened, where, and how to recover (docs/ANTI_SLOP.md rule 5). The
 * page's Chime Studio link sits under the transport; it trims server-side.
 */
export const TRIM_FAILED_MESSAGE =
  "Couldn't trim the file in this browser, so nothing was downloaded. Try again, or open it in Chime Studio to trim and download it there.";

/**
 * The over-limit path. A sound longer than Tesla's 5s cap used to download
 * unchanged while the page said "Trim required" above the button - the trim
 * bounds only ever fed the trim_used analytics flag. The file is now decoded,
 * cut to the visitor's bounds (the trimmer's defaults are 0-5s) and re-encoded
 * as 16-bit PCM WAV at the source sample rate, with the same utilities Chime
 * Studio's export uses. Nothing else is touched: no resampling, no gain.
 */
async function buildTrimmedWav(fileUrl: string, start: number, end: number): Promise<Blob> {
  const source = await storageApi.downloadFile(fileUrl);
  const file = new File([source], TESLA_REQUIREMENTS.FILENAME, {
    type: source.type || "audio/wav",
  });
  const buffer = await decodeAudioFile(file);
  const clipped = trimAudio(buffer, start, Math.min(end, buffer.duration));
  return convertToWav(clipped);
}
import {
  trackSavedSoundsDownloadClick,
  trackDownloadCompleted,
  trackDownloadInitiated,
  trackDownloadButtonClick,
  trackDownloadWithAttribution,
} from "@/lib/tracking";
import { markDownloadCompleted } from "@/lib/download-attribution";

export interface UseSoundDownloadOptions {
  sound: Sound;
  needsTrimming: boolean;
  trimStart: number;
  trimEnd: number;
  /** Optimistic local bump from useRealtimeDownloads. */
  triggerLocalDownload: () => void;
}

export interface UseSoundDownloadResult {
  isDownloading: boolean;
  downloadError: string | null;
  downloadCompleted: boolean;
  /**
   * Byte size of the file actually handed to the browser when it was built
   * here (the trimmed path); null when the catalog file went down unchanged,
   * in which case `sound.size` is the truth.
   */
  deliveredBytes: number | null;
  handleDownload: () => Promise<void>;
}

/**
 * Download state machine extracted from SoundPlayerClient. Completion is
 * surfaced by the adjacent inline receipt, while tracking and error reporting
 * stay independent of the rendered control.
 */
export function useSoundDownload({
  sound,
  needsTrimming,
  trimStart,
  trimEnd,
  triggerLocalDownload,
}: UseSoundDownloadOptions): UseSoundDownloadResult {
  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadCompleted, setDownloadCompleted] = useState(false);
  const [deliveredBytes, setDeliveredBytes] = useState<number | null>(null);
  const { download: downloadFile } = useDownload();

  const handleDownload = async () => {
    if (!sound.file_url) {
      setDownloadError("No file available");
      return;
    }

    const isFromSavedPage =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("from") === "saved";

    trackDownloadButtonClick({
      sound_id: sound.id,
      sound_title: sound.title,
      category: sound.category,
      source: isFromSavedPage ? "saved_page" : "sound_player",
    });

    trackDownloadInitiated({
      sound_id: sound.id,
      sound_title: sound.title,
      category: sound.category,
      source: isFromSavedPage ? "saved_page" : "sound_player",
    });

    if (isFromSavedPage) {
      trackSavedSoundsDownloadClick(sound.id);
    }

    try {
      setIsDownloading(true);
      setDownloadError(null);

      await soundsApi.incrementDownloads(sound.id);
      triggerLocalDownload();

      const urlParams = new URLSearchParams(window.location.search);
      const entrySource = urlParams.get("entry") || undefined;
      const trimUsed =
        needsTrimming &&
        (trimStart !== 0 ||
          trimEnd !==
            Math.min(
              TESLA_REQUIREMENTS.MAX_DURATION,
              sound.duration || TESLA_REQUIREMENTS.MAX_DURATION
            ));

      // Over the 5s cap: honour the "Trim required" the page shows above the
      // button. If the trim cannot be built, say so and download nothing;
      // an untrimmed file is the one outcome the visitor was told not to
      // install.
      let trimmed: Blob | undefined;
      if (needsTrimming) {
        try {
          trimmed = await buildTrimmedWav(sound.file_url, trimStart, trimEnd);
          setDeliveredBytes(trimmed.size);
        } catch (error) {
          Sentry.captureException(error, {
            tags: { context: "SoundPlayerClient", action: "trimForDownload" },
            extra: { soundId: sound?.id, fileUrl: sound?.file_url, trimStart, trimEnd },
          });
          setDownloadError(TRIM_FAILED_MESSAGE);
          return;
        }
      }

      await downloadFile({
        soundId: sound.id,
        ...(trimmed ? { blob: trimmed } : { fileUrl: sound.file_url }),
        filename: TESLA_REQUIREMENTS.FILENAME,
        onSuccess: () => {
          markDownloadCompleted({
            sound_id: sound.id,
            sound_title: sound.title,
            source: isFromSavedPage ? "saved_page" : "sound_player",
          });
          trackDownloadCompleted({
            sound_id: sound.id,
            sound_title: sound.title,
            category: sound.category,
            source: isFromSavedPage ? "saved_page" : "sound_player",
            entry_source: entrySource,
            trim_used: trimUsed,
          });

          trackDownloadWithAttribution({
            sound_id: sound.id,
            sound_title: sound.title,
            category: sound.category,
            source: isFromSavedPage ? "saved_page" : "sound_player",
            entry_source: entrySource,
            trim_used: trimUsed,
          });

          setDownloadCompleted(true);
        },
        onError: error => {
          Sentry.captureException(error, {
            tags: { context: "SoundPlayerClient", action: "handleDownload" },
            extra: { soundId: sound?.id, fileUrl: sound?.file_url },
          });
          setDownloadError("Download failed. Please try again.");
        },
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: "SoundPlayerClient", action: "handleDownload" },
        extra: { soundId: sound?.id, fileUrl: sound?.file_url },
      });
      setDownloadError("Download failed. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, downloadError, downloadCompleted, deliveredBytes, handleDownload };
}
